/**
 * Copy for the marketing (customer contact) module. Owner-facing language
 * only: technical identifiers live in the diagnostics panel, not here.
 * Placeholders use `{name}` and are filled with `fmt()`.
 */

import type { AutomationKind, MessageReason, MessageStatus } from "./types";
import type { ValidationCode } from "./templates";
import type { AlertKind } from "./metrics";

type KindCopy = { name: string; short: string; trigger: string; description: string };

export const marketingCopyEn = {
  pageTitle: "Marketing",
  home: {
    history: "Message history",
    settings: "Settings",
    periodLabel: "Period",
    thisMonth: "This month",
    last30: "30 days",
    last90: "90 days",
    sent: "Messages sent",
    planned: "Scheduled messages",
    plannedHint: "Still to be sent",
    cost: "Message cost",
    automationsTitle: "Automated messages",
    draft: "Draft",
    draftLabel: "Unpublished changes",
    setUp: "Set up",
    review: "Review",
    connect: "Connect",
    smsNotConnected: "SMS isn't connected yet — messages won't go out until you connect it.",
    budgetPaused: "{count:# marketing message is|# marketing messages are} paused — this month's budget is used up.",
    failed: "{count:# message|# messages} couldn't be delivered this week.",
    missing: "{count:# message was|# messages were} stopped because details were missing.",
    back: "Marketing",
  },
  show: {
    label: "Show",
    all: "All",
    planned: "Scheduled",
    sent: "Sent",
    notSent: "Not sent",
    failed: "Failed",
  },
  pageSubtitle: "Automatic messages that keep you in touch with your customers.",
  banner: {
    title: "Demo mode — messages are not actually sent",
    demoData: "Demo data only · simulated clock: {time}",
    salonData: "Your salon's calendar · settings saved on this device · upcoming messages are a forecast",
    simulated: "SMS connection, delivery and costs are simulated",
  },
  mode: {
    label: "Data source",
    salon: "My salon",
    demo: "Demo",
    actingAs: "Acting as",
  },
  tabs: {
    label: "Marketing sections",
    overview: "Customer contact",
    automations: "Automated messages",
    history: "Message history",
    simulator: "Demo simulator",
  },
  range: { label: "Period", d7: "7 days", d30: "30 days", d90: "90 days" },
  sms: {
    connected: "SMS connected",
    notConnected: "SMS not connected",
    error: "SMS connection problem",
    settings: "SMS settings",
  },
  common: {
    close: "Close",
    back: "Back",
    save: "Save",
    cancel: "Cancel",
    edit: "Edit",
    open: "Open",
    view: "View",
    all: "All",
    none: "None",
    days: "days",
    perMonth: "per month",
    estimated: "estimated",
    actual: "actual",
    simulated: "simulated",
    noPrice: "No price saved",
    unknownCustomer: "Customer not found",
    test: "Test",
    version: "Version {n}",
    by: "{who} · {when}",
    units: "{n:# SMS unit|# SMS units}",
    unitsShort: "{n:# unit|# units}",
    yes: "Yes",
    no: "No",
  },
  kinds: {
    booking_confirmation: {
      name: "Booking confirmation",
      short: "Confirmation",
      trigger: "Right after booking",
      description: "One message per visit, even with several services.",
    },
    appointment_reminder: {
      name: "Appointment reminder",
      short: "Reminder",
      trigger: "The day before, at {time}",
      description: "If the visit was booked after that moment, only the confirmation is sent.",
    },
    appointment_rescheduled: {
      name: "Appointment change",
      short: "Change",
      trigger: "Right after the time changes",
      description: "The old reminder is cancelled and a new one is scheduled.",
    },
    appointment_cancelled: {
      name: "Appointment cancellation",
      short: "Cancellation",
      trigger: "Right after cancellation",
      description: "Pending messages for that visit are cancelled.",
    },
    rebooking_invite: {
      name: "Invitation to come back",
      short: "Come back",
      trigger: "{days} days after the visit, at {time}",
      description: "Only to customers with marketing consent. Not sent if a matching visit is already booked.",
    },
  } as Record<AutomationKind, KindCopy>,
  rebookingNoInterval: "Not set up yet",
  categories: {
    transactional: "Service message",
    marketing: "Marketing message",
  },
  categoryHints: {
    transactional: "Not limited by marketing rules. Sent unless the customer opted out of SMS.",
    marketing: "Requires marketing consent · sent only {start}–{end} · at most 1 per {days} days per customer.",
  },
  automationState: {
    active: "Active",
    off: "Off",
    draft: "Unpublished draft",
    attention: "Needs attention",
  },
  statuses: {
    scheduled: "Scheduled",
    deferred: "Postponed",
    paused: "Paused",
    sent: "Sent",
    delivered: "Delivered",
    unknown: "Awaiting confirmation",
    failed: "Failed",
    cancelled: "Cancelled",
    skipped: "Not sent",
  } as Record<MessageStatus, string>,
  reasons: {
    outside_send_window: "Outside the marketing hours ({start}–{end}). Moved to the next allowed time.",
    frequency_cap: "This customer got a marketing message recently. Postponed to respect the 1 per {days} days limit.",
    frequency_cap_expired: "Would have been postponed too long by the frequency limit, so it was not sent.",
    budget_cap: "The monthly marketing budget is used up. Paused until you raise the budget or the month resets.",
    consent_marketing_missing: "No marketing consent on record. Without consent, marketing messages are not sent.",
    consent_opted_out: "The customer asked not to receive these messages.",
    no_phone: "No phone number on the customer card.",
    missing_variable: "Required details are missing ({detail}), so the message was stopped.",
    appointment_cancelled: "The appointment was cancelled.",
    appointment_rescheduled: "The appointment time changed. A new message replaces this one.",
    appointment_passed: "The appointment time has already passed.",
    appointment_not_completed: "The visit was not completed, so no invitation is needed.",
    reminder_window_passed: "The appointment was booked after the reminder time — the confirmation covers it.",
    automation_disabled: "The automation was turned off.",
    future_booking_exists: "The customer already booked a matching visit.",
    superseded_by_new_visit: "A newer visit of the same service started a new countdown.",
    merged_into: "Combined with another invitation to the same customer on the same day.",
    provider_temporary_failure: "The SMS service had a temporary problem. Retrying the same message.",
    provider_permanent_failure: "The SMS service rejected the message.",
    delivery_failed: "The carrier could not deliver the message to this phone.",
    awaiting_provider_confirmation: "Sent to the SMS service without a clear answer yet. We check its status — it is not resent automatically.",
    sms_not_connected: "SMS is not connected. Messages wait until you connect.",
    service_excluded: "This service is excluded from the automation.",
    manually_cancelled: "Cancelled manually.",
  } as Record<MessageReason, string>,
  overview: {
    periodTitle: "Activity in the last {days} days",
    periodNote: "Delivered and failed are stages of the sent messages — they are not added on top.",
    futureTitle: "Upcoming messages",
    futureNote: "Messages planned from now on. Not counted in the period activity.",
    automationsTitle: "Automations",
    attentionTitle: "Needs your attention",
    allClear: "Nothing needs your attention right now.",
    upcomingEmpty: "No messages are planned yet.",
    upcomingEmptySalon: "Upcoming messages appear here once there are booked visits in the calendar.",
    seeAllHistory: "Full history",
    manageAutomations: "Manage messages",
    budgetTitle: "Marketing budget this month",
    budgetOf: "{spent} of {limit}",
    budgetNote: "Service messages are not counted against this budget.",
    forecastBadge: "Forecast",
    nextMessage: "Next",
    perAutomationSent: "{n} sent",
    perAutomationNext: "Next: {when}",
    perAutomationNone: "Nothing planned",
  },
  kpi: {
    sent: "Sent",
    sentSub: "{delivered} delivered · {awaiting} waiting for confirmation",
    deliveryRate: "Delivered",
    deliveryRateSub: "of the messages sent",
    failed: "Failed",
    failedSub: "Not delivered or rejected",
    notSent: "Stopped before sending",
    notSentSub: "Consent, missing details or no longer relevant",
    cost: "Cost",
    costSub: "Actual {actual} · estimated {estimated} · simulated",
    scheduled: "Scheduled",
    deferred: "Postponed",
    paused: "Paused",
    futureCost: "Estimated cost",
  },
  alerts: {
    sms_not_connected: { title: "SMS is not connected", body: "No message can be sent until SMS is connected.", action: "Connect SMS" },
    budget_paused: { title: "Marketing messages paused", body: "{count:# message is|# messages are} waiting because the monthly budget is used up.", action: "Budget settings" },
    budget_near_limit: { title: "Budget almost used", body: "{percent}% of this month's marketing budget is used.", action: "Budget settings" },
    failed_recent: { title: "Messages failed this week", body: "{count:# message|# messages} could not be delivered.", action: "Review" },
    unknown_pending: { title: "Waiting for SMS service confirmation", body: "{count:# message was|# messages were} sent without a clear answer yet. {count:It|They} will not be resent automatically.", action: "Review" },
    missing_variables: { title: "Messages stopped for missing details", body: "{count:# message was|# messages were} stopped because required details were missing.", action: "Review" },
    drafts_unpublished: { title: "Unpublished changes", body: "{count:# automation has|# automations have} a draft. The active version keeps working until you publish.", action: "Open" },
    rebooking_off: { title: "Invitation to come back is off", body: "Choose an interval and turn it on to bring customers back on time.", action: "Set up" },
    consent_gaps: { title: "Customers without marketing consent", body: "{count:# invitation was|# invitations were} not sent this month for missing consent.", action: "Review" },
  } as Record<AlertKind, { title: string; body: string; action: string }>,
  firstRun: {
    title: "Set up customer SMS",
    subtitle: "Four short steps. Everything here is simulated — nothing is sent and nothing is charged.",
    stepConnect: "Connect SMS",
    stepConnectBody: "Connect a simulated SMS account to see how sending will work.",
    connectButton: "Connect (simulated)",
    connected: "Connected · simulated",
    stepProfile: "Time zone and sender name",
    stepProfileBody: "Reminders and marketing hours follow the salon's time zone.",
    stepPreview: "Check how a message looks",
    stepPreviewBody: "This is the booking confirmation your customers will get.",
    stepTest: "Send yourself a test",
    stepTestBody: "A simulated test goes to the phone number you enter.",
    finish: "Finish setup",
    finishHint: "Connect SMS and send a test to finish.",
    done: "Setup complete",
    stepOf: "Step {n} of {total}",
  },
  editor: {
    backToList: "Back to marketing",
    moreOptions: "More options",
    moreVariables: "More details",
    fewerVariables: "Fewer",
    marketingNote: "Sent only to customers who agreed to marketing, between {start} and {end}.",
    length: "{chars} characters · {n:1 SMS|# SMS}",
    activeVersion: "Active version {n}",
    publishedBy: "Published by {who} · {when}",
    draftBy: "Draft saved by {who} · {when}",
    noDraft: "No draft — you are editing a copy of the active version.",
    unsaved: "Unsaved changes",
    saveDraft: "Save draft",
    discardDraft: "Discard draft",
    publish: "Publish changes",
    publishConfirm: "Publish? The active version will be replaced and {count:# pending message|# pending messages} will use the new text and timing.",
    published: "Published. {count:# pending message was|# pending messages were} updated.",
    draftSaved: "Draft saved. The active version keeps working until you publish.",
    draftDiscarded: "Draft discarded.",
    cannotPublish: "Fix the errors before publishing.",
    enabledLabel: "Automation on",
    whenTitle: "When it is sent",
    immediate: "Right away",
    reminderDayBefore: "Day before, at",
    rebookingInterval: "Days after the visit",
    rebookingTime: "Send at",
    customInterval: "Custom",
    textTitle: "Message text",
    language: "Language",
    languageFallback: "No {lang} text yet — customers who prefer {lang} get the {fallback} text. Nothing is auto-translated.",
    variablesTitle: "Insert detail",
    variablesHint: "Details are filled in for each customer. Only details we actually have are offered.",
    optionalVariable: "optional",
    chars: "{n} characters",
    segments: "{n:# SMS unit|# SMS units} ({encoding})",
    costEstimate: "≈ {cost} per message · estimated",
    optOutAppended: "An opt-out link is added automatically to marketing messages.",
    previewTitle: "Preview",
    previewDraft: "Draft",
    previewActive: "Active",
    previewSample: "Example customer",
    previewFictional: "Example data",
    previewMissing: "Missing for this customer: {list}. A message with missing required details is not sent.",
    previewFallbackLink: "No online booking link yet — the salon phone is used instead.",
    permissionEdit: "You can view this automation but not edit it.",
    permissionPublish: "You can save drafts. Publishing and turning automations on or off need publish permission.",
    servicesTitle: "Per-service changes",
    servicesSubtitle: "Every service uses the general message. Change only where needed.",
    historyTitle: "Change history",
    noHistory: "No changes recorded yet.",
    rebookingEnableBlocked: "Choose after how many days to send and publish — then you can turn it on.",
    toggledOn: "Automation turned on.",
    toggledOff: "Automation turned off. {count:# pending message was|# pending messages were} cancelled.",
  },
  validation: {
    empty_default_body: "The {lang} text (default language) is empty.",
    missing_language_body: "No {lang} text — the default language will be used.",
    unknown_token: "Unknown detail “{detail}”. Use the insert buttons.",
    unavailable_token: "“{detail}” is not available in this message type.",
    interval_required: "Choose the number of days after a visit.",
    invalid_time: "Choose a valid time.",
    opt_out_appended: "An opt-out link will be added at the end.",
    missing_first_name_token: "Consider adding the customer's first name.",
  } as Record<ValidationCode, string>,
  overrides: {
    inherited: "Default",
    customized: "Customized",
    included: "Include this service",
    excluded: "Excluded",
    customText: "Custom text ({lang})",
    customTextHint: "Leave empty to use the general text. Visits with several services always use the general text.",
    interval: "Days after the visit",
    intervalHint: "Empty = general interval ({days} days)",
    price: "Price in message",
    priceInherit: "From the booking / service",
    priceCustom: "Custom price",
    priceHide: "Do not show a price",
    priceMissing: "No price saved for this service — price is not shown (never as 0).",
    reset: "Reset to default",
    noServices: "No services in the catalog yet.",
    multiServiceNote: "A visit with several services gets one message with the general text and the list of services.",
  },
  test: {
    title: "Send a test",
    phone: "Your phone",
    version: "Text to test",
    outcome: "Simulated result",
    outcomeNormal: "Delivered",
    outcomeTemporary: "Temporary failure",
    outcomeTimeout: "No clear answer",
    outcomePermanent: "Rejected",
    send: "Send test",
    sent: "Test sent to {phone}.",
    resultTitle: "Last test",
    needsConnection: "Connect SMS to send a test.",
    forbidden: "You don't have permission to send tests.",
  },
  history: {
    search: "Search customer or phone",
    automation: "Message type",
    status: "Status",
    period: "Date",
    periodAll: "All dates",
    periodUpcoming: "Upcoming",
    periodToday: "Today",
    period7: "Last 7 days",
    period30: "Last 30 days",
    includeTests: "Show tests",
    empty: "No messages match these filters.",
    emptySalon: "No messages yet. They will appear here once visits are booked.",
    clear: "Clear filters",
    count: "{n:# message|# messages}",
    colCustomer: "Customer",
    colAutomation: "Message",
    colVisit: "Visit",
    colPlanned: "Planned",
    colSent: "Sent",
    colStatus: "Status",
    colUnits: "Units",
    colCost: "Cost",
    forecast: "Forecast",
  },
  drawer: {
    sentText: "Exact text that was sent",
    plannedText: "Planned text",
    plannedTextNote: "The final text is checked again right before sending: appointment, consent, automation and details.",
    projectedBlock: "If it were due now, it would not be sent: {reason}",
    details: "Details",
    customer: "Customer",
    phone: "Phone",
    visit: "Visit",
    planned: "Planned for",
    nextTry: "Next attempt",
    sentAt: "Sent",
    deliveredAt: "Delivered",
    failedAt: "Failed",
    units: "SMS units",
    cost: "Cost",
    costEstimated: "{cost} · estimated",
    costActual: "{cost} · actual",
    versionUsed: "Text version",
    language: "Language",
    timeline: "What happened",
    created: "Message planned",
    attempt: "Attempt {n}",
    attemptAccepted: "Accepted by the SMS service",
    attemptTemporary: "Temporary problem at the SMS service",
    attemptPermanent: "Rejected by the SMS service",
    attemptTimeout: "No clear answer from the SMS service",
    reconciledDelivered: "Status check: delivered",
    reconciledNotReceived: "Status check: not received",
    deliveredEvent: "Delivered to the phone",
    failedEvent: "Not delivered",
    stoppedEvent: "Stopped",
    deferredEvent: "Postponed to {when}",
    mergedServices: "Includes services merged from another invitation: {list}",
    mergedInto: "Combined into another message",
    openMerged: "Open the combined message",
    cancel: "Cancel message",
    cancelled: "Message cancelled.",
    resume: "Try again now",
    checkStatus: "Check status",
    simulateDelivered: "Simulate: delivered",
    simulateNotReceived: "Simulate: not received",
    unknownNote: "We don't resend while the status is unclear, so the customer never gets the same message twice. A status check decides what happens next.",
    diagnostics: "Diagnostics (technical)",
    diagnosticsNote: "For support only. Customers never see this.",
    testBadge: "Test",
    testTo: "Test to {phone}",
  },
  settings: {
    title: "Message settings",
    subtitle: "Connection, sender, hours and budget.",
    sectionSender: "Sender and language",
    sectionMarketing: "Marketing messages",
    sectionBudget: "Budget and sending",
    budgetUsed: "{spent} of {limit} used this month.",
    connection: "Connection",
    connectedAs: "Connected to {provider}",
    connect: "Connect SMS",
    disconnect: "Disconnect",
    senderName: "Sender name",
    senderHint: "3–11 English letters or digits, as customers will see it.",
    senderInvalid: "Use 3–11 English letters or digits.",
    timezone: "Salon time zone",
    defaultLanguage: "Default language",
    languages: "Message languages",
    languagesHint: "Each language has its own saved text. Nothing is auto-translated.",
    window: "Marketing hours",
    windowHint: "Marketing messages outside these hours are moved to the next allowed time. Service messages are not limited.",
    windowFrom: "From",
    windowTo: "To",
    frequency: "Marketing frequency limit",
    frequencyHint: "At most one marketing message per customer in this many days.",
    budget: "Monthly marketing budget (₪)",
    budgetHint: "When reached, marketing messages pause and you get an alert.",
    alertAt: "Alert at (% of budget)",
    attempts: "Max attempts per message",
    attemptsHint: "Retries only after a temporary failure, always on the same message.",
    unitCost: "Estimated price per SMS unit (₪)",
    unitCostHint: "Used for estimates. Actual cost comes from the provider (simulated here).",
    save: "Save settings",
    saved: "Settings saved.",
    forbidden: "Only the owner can change SMS settings.",
    he: "Hebrew",
    en: "English",
  },
  simulator: {
    title: "Demo simulator",
    subtitle: "Drive the demo world: move the clock, create calendar events and choose how the SMS service answers. Affects demo data only.",
    clock: "Simulated clock",
    plus15: "+15 min",
    plus1h: "+1 hour",
    plus1d: "+1 day",
    nextEvent: "Jump to next event",
    noNextEvent: "Nothing is scheduled.",
    provider: "Next SMS service answer",
    providerHint: "Applies once, to the next message that is sent.",
    role: "Acting as",
    roleHint: "See how permissions change what each person can do.",
    reset: "Reset demo data",
    resetConfirm: "Reset all demo data? Salon settings are not affected.",
    resetDone: "Demo data was reset.",
    restartFirstRun: "Show first-time setup again",
    book: "Book a visit",
    bookCustomer: "Customer",
    bookServices: "Services",
    bookDay: "In how many days",
    bookTime: "Time",
    bookButton: "Book",
    booked: "Visit booked. Confirmation handled by the automation.",
    upcoming: "Upcoming visits",
    noUpcoming: "No upcoming visits.",
    reschedule: "+1 day",
    cancelVisit: "Cancel",
    complete: "Mark completed",
    noShow: "No-show",
    customers: "Customers and consent",
    marketingConsent: "Marketing",
    transactionalConsent: "Service SMS",
    consentGranted: "Approved",
    consentRevoked: "Opted out",
    consentUnknown: "No record",
    noPhone: "No phone",
    undeliverable: "Phone can't receive",
    done: "Done.",
  },
  permissions: {
    noAccessTitle: "No access to marketing",
    noAccessBody: "Only the salon owner or a manager with permission can manage customer messages.",
    viewOnly: "View only",
    editOnly: "Drafts only",
  },
  empty: {
    salonNoData: "No visits in your calendar yet",
    salonNoDataBody: "When visits are booked, the messages they will trigger appear here. You can explore everything with demo data.",
    openDemo: "Explore with demo data",
  },
  errors: {
    forbidden: "You don't have permission for this action.",
    invalid: "Some details are not valid.",
    interval_required: "Choose and publish an interval before turning this on.",
    not_connected: "Connect SMS first.",
    demo_only: "Available in demo mode only.",
    not_found: "Nothing to update.",
  },
  audit: {
    draft_saved: "Draft saved",
    draft_discarded: "Draft discarded",
    published: "Published",
    enabled: "Turned on",
    disabled: "Turned off",
    settings_updated: "Settings updated",
    sms_connected: "SMS connected",
    sms_disconnected: "SMS disconnected",
    test_sent: "Test sent",
    message_cancelled: "Message cancelled",
    message_reconciled: "Status checked",
    message_resumed: "Message resumed",
    demo_reset: "Demo reset",
  },
};

export type MarketingCopy = typeof marketingCopyEn;

export const marketingCopyHe: MarketingCopy = {
  pageTitle: "שיווק",
  home: {
    history: "היסטוריית הודעות",
    settings: "הגדרות",
    periodLabel: "תקופה",
    thisMonth: "החודש",
    last30: "30 יום",
    last90: "90 יום",
    sent: "הודעות שנשלחו",
    planned: "הודעות מתוכננות",
    plannedHint: "יישלחו בהמשך",
    cost: "עלות הודעות",
    automationsTitle: "הודעות אוטומטיות",
    draft: "טיוטה",
    draftLabel: "יש שינויים שלא פורסמו",
    setUp: "להגדרה",
    review: "לבדיקה",
    connect: "לחיבור",
    smsNotConnected: "ה־SMS עוד לא מחובר — הודעות לא יישלחו עד החיבור.",
    budgetPaused: "{count:הודעה שיווקית אחת מושהית|# הודעות שיווקיות מושהות} — תקציב החודש נוצל.",
    failed: "{count:הודעה אחת לא נמסרה|# הודעות לא נמסרו} השבוע.",
    missing: "{count:הודעה אחת נעצרה|# הודעות נעצרו} בגלל פרטים חסרים.",
    back: "שיווק",
  },
  show: {
    label: "הצגה",
    all: "הכול",
    planned: "מתוכננות",
    sent: "נשלחו",
    notSent: "לא נשלחו",
    failed: "נכשלו",
  },
  pageSubtitle: "הודעות אוטומטיות ששומרות על הקשר עם הלקוחות.",
  banner: {
    title: "מצב הדגמה — הודעות אינן נשלחות בפועל",
    demoData: "נתוני הדגמה בלבד · שעון מדומה: {time}",
    salonData: "היומן של הסלון · ההגדרות נשמרות במכשיר הזה · הודעות קרובות מוצגות כתחזית",
    simulated: "חיבור ה־SMS, המסירה והעלויות מדומים",
  },
  mode: {
    label: "מקור נתונים",
    salon: "הסלון שלי",
    demo: "הדגמה",
    actingAs: "צפייה בתור",
  },
  tabs: {
    label: "אזורי שיווק",
    overview: "קשר עם לקוחות",
    automations: "הודעות אוטומטיות",
    history: "היסטוריית הודעות",
    simulator: "סימולטור הדגמה",
  },
  range: { label: "תקופה", d7: "7 ימים", d30: "30 יום", d90: "90 יום" },
  sms: {
    connected: "SMS מחובר",
    notConnected: "SMS לא מחובר",
    error: "בעיה בחיבור SMS",
    settings: "הגדרות SMS",
  },
  common: {
    close: "סגירה",
    back: "חזרה",
    save: "שמירה",
    cancel: "ביטול",
    edit: "עריכה",
    open: "פתיחה",
    view: "צפייה",
    all: "הכול",
    none: "ללא",
    days: "ימים",
    perMonth: "לחודש",
    estimated: "משוער",
    actual: "בפועל",
    simulated: "מדומה",
    noPrice: "אין מחיר שמור",
    unknownCustomer: "הלקוחה לא נמצאה",
    test: "בדיקה",
    version: "גרסה {n}",
    by: "{who} · {when}",
    units: "{n:יחידת SMS אחת|# יחידות SMS}",
    unitsShort: "{n} יח׳",
    yes: "כן",
    no: "לא",
  },
  kinds: {
    booking_confirmation: {
      name: "אישור קביעת תור",
      short: "אישור",
      trigger: "מיד אחרי קביעת התור",
      description: "הודעה אחת לכל ביקור, גם כשיש בו כמה שירותים.",
    },
    appointment_reminder: {
      name: "תזכורת לתור",
      short: "תזכורת",
      trigger: "יום לפני התור, בשעה {time}",
      description: "אם התור נקבע אחרי מועד התזכורת, נשלח רק אישור.",
    },
    appointment_rescheduled: {
      name: "שינוי תור",
      short: "שינוי",
      trigger: "מיד אחרי שינוי מועד התור",
      description: "התזכורת הישנה מבוטלת ונקבעת תזכורת חדשה.",
    },
    appointment_cancelled: {
      name: "ביטול תור",
      short: "ביטול",
      trigger: "מיד אחרי ביטול התור",
      description: "הודעות ממתינות לאותו ביקור מבוטלות.",
    },
    rebooking_invite: {
      name: "הזמנה לחזור",
      short: "הזמנה לחזור",
      trigger: "{days} ימים אחרי הביקור, בשעה {time}",
      description: "רק ללקוחות עם הסכמה שיווקית. לא נשלחת אם כבר נקבע ביקור מתאים.",
    },
  },
  rebookingNoInterval: "עדיין לא נקבע מתי לשלוח",
  categories: {
    transactional: "הודעת שירות",
    marketing: "הודעה שיווקית",
  },
  categoryHints: {
    transactional: "לא כפופה לכללי השיווק. נשלחת אלא אם הלקוחה ביקשה לא לקבל SMS.",
    marketing: "דורשת הסכמה שיווקית · נשלחת רק בין {start} ל־{end} · עד הודעה אחת ל־{days} ימים ללקוחה.",
  },
  automationState: {
    active: "פעילה",
    off: "כבויה",
    draft: "טיוטה שלא פורסמה",
    attention: "דורשת טיפול",
  },
  statuses: {
    scheduled: "מתוזמנת",
    deferred: "נדחתה",
    paused: "מושהית",
    sent: "נשלחה",
    delivered: "נמסרה",
    unknown: "ממתינה לאישור",
    failed: "נכשלה",
    cancelled: "בוטלה",
    skipped: "לא נשלחה",
  },
  reasons: {
    outside_send_window: "מחוץ לשעות השיווק ({start}–{end}). הועברה למועד המותר הבא.",
    frequency_cap: "הלקוחה קיבלה הודעה שיווקית לאחרונה. נדחתה כדי לשמור על עד הודעה אחת ל־{days} ימים.",
    frequency_cap_expired: "מגבלת התדירות הייתה דוחה אותה יותר מדי זמן, ולכן לא נשלחה.",
    budget_cap: "תקציב השיווק החודשי נוצל. מושהית עד שתגדילי את התקציב או עד תחילת החודש הבא.",
    consent_marketing_missing: "אין הסכמה שיווקית רשומה. בלי הסכמה לא שולחים הודעות שיווקיות.",
    consent_opted_out: "הלקוחה ביקשה לא לקבל הודעות כאלה.",
    no_phone: "אין מספר טלפון בכרטיס הלקוחה.",
    missing_variable: "חסרים פרטי חובה ({detail}), ולכן ההודעה נעצרה.",
    appointment_cancelled: "התור בוטל.",
    appointment_rescheduled: "שעת התור השתנתה. הודעה חדשה מחליפה את זו.",
    appointment_passed: "מועד התור כבר עבר.",
    appointment_not_completed: "הביקור לא הושלם, ולכן אין צורך בהזמנה.",
    reminder_window_passed: "התור נקבע אחרי מועד התזכורת — האישור מכסה אותו.",
    automation_disabled: "האוטומציה כובתה.",
    future_booking_exists: "הלקוחה כבר קבעה ביקור מתאים.",
    superseded_by_new_visit: "ביקור חדש באותו שירות התחיל ספירה חדשה.",
    merged_into: "אוחדה עם הזמנה אחרת לאותה לקוחה באותו יום.",
    provider_temporary_failure: "תקלה זמנית בשירות ה־SMS. מנסים שוב את אותה הודעה.",
    provider_permanent_failure: "שירות ה־SMS דחה את ההודעה.",
    delivery_failed: "חברת הסלולר לא הצליחה למסור את ההודעה לטלפון הזה.",
    awaiting_provider_confirmation: "נשלחה לשירות ה־SMS ועדיין אין תשובה ברורה. בודקים את הסטטוס — לא שולחים שוב אוטומטית.",
    sms_not_connected: "ה־SMS לא מחובר. ההודעות ממתינות עד לחיבור.",
    service_excluded: "השירות הוחרג מהאוטומציה.",
    manually_cancelled: "בוטלה ידנית.",
  },
  overview: {
    periodTitle: "פעילות ב־{days} הימים האחרונים",
    periodNote: "נמסרו ונכשלו הם שלבים של ההודעות שנשלחו — הם לא מתווספים לסך.",
    futureTitle: "הודעות קרובות",
    futureNote: "הודעות שמתוכננות מעכשיו והלאה. לא נספרות בפעילות התקופה.",
    automationsTitle: "אוטומציות",
    attentionTitle: "דורש את תשומת לבך",
    allClear: "אין כרגע משהו שדורש טיפול.",
    upcomingEmpty: "עדיין אין הודעות מתוכננות.",
    upcomingEmptySalon: "הודעות קרובות יופיעו כאן כשיהיו ביקורים ביומן.",
    seeAllHistory: "להיסטוריה המלאה",
    manageAutomations: "ניהול הודעות",
    budgetTitle: "תקציב שיווק החודש",
    budgetOf: "{spent} מתוך {limit}",
    budgetNote: "הודעות שירות לא נספרות בתקציב הזה.",
    forecastBadge: "תחזית",
    nextMessage: "הבאה",
    perAutomationSent: "{n} נשלחו",
    perAutomationNext: "הבאה: {when}",
    perAutomationNone: "אין הודעות מתוכננות",
  },
  kpi: {
    sent: "נשלחו",
    sentSub: "{delivered} נמסרו · {awaiting} ממתינות לאישור",
    deliveryRate: "נמסרו",
    deliveryRateSub: "מתוך ההודעות שנשלחו",
    failed: "נכשלו",
    failedSub: "לא נמסרו או נדחו",
    notSent: "נעצרו לפני שליחה",
    notSentSub: "הסכמה, פרטים חסרים או שכבר לא רלוונטי",
    cost: "עלות",
    costSub: "בפועל {actual} · משוער {estimated} · מדומה",
    scheduled: "מתוזמנות",
    deferred: "נדחו",
    paused: "מושהות",
    futureCost: "עלות משוערת",
  },
  alerts: {
    sms_not_connected: { title: "ה־SMS לא מחובר", body: "אי אפשר לשלוח הודעות עד שה־SMS יחובר.", action: "חיבור SMS" },
    budget_paused: { title: "הודעות שיווק מושהות", body: "{count:הודעה אחת ממתינה|# הודעות ממתינות} כי תקציב החודש נוצל.", action: "הגדרות תקציב" },
    budget_near_limit: { title: "התקציב כמעט נוצל", body: "נוצלו {percent}% מתקציב השיווק של החודש.", action: "הגדרות תקציב" },
    failed_recent: { title: "הודעות שנכשלו השבוע", body: "{count:הודעה אחת לא נמסרה|# הודעות לא נמסרו}.", action: "לבדיקה" },
    unknown_pending: { title: "ממתינות לאישור משירות ה־SMS", body: "{count:הודעה אחת נשלחה|# הודעות נשלחו} ועדיין אין תשובה ברורה. {count:היא לא תישלח|הן לא יישלחו} שוב אוטומטית.", action: "לבדיקה" },
    missing_variables: { title: "הודעות שנעצרו בגלל פרטים חסרים", body: "{count:הודעה אחת נעצרה|# הודעות נעצרו} כי חסרו פרטי חובה.", action: "לבדיקה" },
    drafts_unpublished: { title: "שינויים שלא פורסמו", body: "{count:לאוטומציה אחת|ל־# אוטומציות} יש טיוטה. הגרסה הפעילה ממשיכה לעבוד עד הפרסום.", action: "פתיחה" },
    rebooking_off: { title: "ההזמנה לחזור כבויה", body: "בחרי מרווח זמן והפעילי כדי להחזיר לקוחות בזמן.", action: "להגדרה" },
    consent_gaps: { title: "לקוחות בלי הסכמה שיווקית", body: "{count:הזמנה אחת לא נשלחה|# הזמנות לא נשלחו} החודש בגלל העדר הסכמה.", action: "לבדיקה" },
  },
  firstRun: {
    title: "הגדרת SMS ללקוחות",
    subtitle: "ארבעה צעדים קצרים. הכול כאן מדומה — שום הודעה לא נשלחת ושום דבר לא מחויב.",
    stepConnect: "חיבור SMS",
    stepConnectBody: "חברי חשבון SMS מדומה כדי לראות איך השליחה תעבוד.",
    connectButton: "חיבור (מדומה)",
    connected: "מחובר · מדומה",
    stepProfile: "אזור זמן ושם שולח",
    stepProfileBody: "התזכורות ושעות השיווק נקבעות לפי אזור הזמן של הסלון.",
    stepPreview: "בדיקה איך ההודעה נראית",
    stepPreviewBody: "זה אישור קביעת התור שהלקוחות יקבלו.",
    stepTest: "שליחת ניסיון לעצמך",
    stepTestBody: "ניסיון מדומה יישלח למספר שתזיני.",
    finish: "סיום ההגדרה",
    finishHint: "כדי לסיים צריך לחבר SMS ולשלוח ניסיון.",
    done: "ההגדרה הושלמה",
    stepOf: "שלב {n} מתוך {total}",
  },
  editor: {
    backToList: "חזרה לשיווק",
    moreOptions: "אפשרויות נוספות",
    moreVariables: "פרטים נוספים",
    fewerVariables: "פחות",
    marketingNote: "נשלחת רק ללקוחות שאישרו דיוור, בין {start} ל־{end}.",
    length: "{chars} תווים · {n:הודעת SMS אחת|# הודעות SMS}",
    activeVersion: "גרסה פעילה {n}",
    publishedBy: "פורסמה ע״י {who} · {when}",
    draftBy: "טיוטה נשמרה ע״י {who} · {when}",
    noDraft: "אין טיוטה — את עורכת עותק של הגרסה הפעילה.",
    unsaved: "שינויים שלא נשמרו",
    saveDraft: "שמירת טיוטה",
    discardDraft: "מחיקת הטיוטה",
    publish: "פרסום שינויים",
    publishConfirm: "לפרסם? הגרסה הפעילה תוחלף ו{count:הודעה ממתינה אחת תשתמש|־# הודעות ממתינות ישתמשו} בנוסח ובתזמון החדשים.",
    published: "פורסם. {count:הודעה ממתינה אחת עודכנה|# הודעות ממתינות עודכנו}.",
    draftSaved: "הטיוטה נשמרה. הגרסה הפעילה ממשיכה לעבוד עד הפרסום.",
    draftDiscarded: "הטיוטה נמחקה.",
    cannotPublish: "יש לתקן את השגיאות לפני פרסום.",
    enabledLabel: "האוטומציה פעילה",
    whenTitle: "מתי נשלחת",
    immediate: "מיד",
    reminderDayBefore: "יום לפני, בשעה",
    rebookingInterval: "ימים אחרי הביקור",
    rebookingTime: "שעת שליחה",
    customInterval: "אחר",
    textTitle: "נוסח ההודעה",
    language: "שפה",
    languageFallback: "אין עדיין נוסח ב{lang} — לקוחות שמעדיפות {lang} יקבלו את הנוסח ב{fallback}. אין תרגום אוטומטי.",
    variablesTitle: "הוספת פרט",
    variablesHint: "הפרטים מתמלאים לכל לקוחה. מוצעים רק פרטים שבאמת קיימים במערכת.",
    optionalVariable: "רשות",
    chars: "{n} תווים",
    segments: "{n:יחידת SMS אחת|# יחידות SMS} ({encoding})",
    costEstimate: "≈ {cost} להודעה · משוער",
    optOutAppended: "להודעות שיווקיות מתווסף אוטומטית קישור להסרה.",
    previewTitle: "תצוגה מקדימה",
    previewDraft: "טיוטה",
    previewActive: "פעילה",
    previewSample: "לקוחה לדוגמה",
    previewFictional: "נתוני דוגמה",
    previewMissing: "חסר ללקוחה הזו: {list}. הודעה שחסרים בה פרטי חובה לא נשלחת.",
    previewFallbackLink: "אין עדיין קישור לתורים אונליין — במקומו מוצג טלפון הסלון.",
    permissionEdit: "אפשר לצפות באוטומציה אבל לא לערוך אותה.",
    permissionPublish: "אפשר לשמור טיוטות. פרסום והפעלה/כיבוי דורשים הרשאת פרסום.",
    servicesTitle: "התאמה לפי שירות",
    servicesSubtitle: "כל שירות משתמש בהודעה הכללית. אפשר לשנות רק איפה שצריך.",
    historyTitle: "היסטוריית שינויים",
    noHistory: "עדיין לא נרשמו שינויים.",
    rebookingEnableBlocked: "בוחרים אחרי כמה ימים לשלוח ומפרסמים — ואז אפשר להפעיל.",
    toggledOn: "האוטומציה הופעלה.",
    toggledOff: "האוטומציה כובתה. {count:הודעה ממתינה אחת בוטלה|# הודעות ממתינות בוטלו}.",
  },
  validation: {
    empty_default_body: "הנוסח ב{lang} (שפת ברירת המחדל) ריק.",
    missing_language_body: "אין נוסח ב{lang} — ישמש נוסח שפת ברירת המחדל.",
    unknown_token: "פרט לא מוכר „{detail}”. השתמשי בכפתורי ההוספה.",
    unavailable_token: "„{detail}” לא זמין בסוג ההודעה הזה.",
    interval_required: "בחרי מספר ימים אחרי ביקור.",
    invalid_time: "בחרי שעה תקינה.",
    opt_out_appended: "בסוף ההודעה יתווסף קישור להסרה.",
    missing_first_name_token: "כדאי להוסיף את השם הפרטי של הלקוחה.",
  },
  overrides: {
    inherited: "ברירת מחדל",
    customized: "מותאם",
    included: "לכלול את השירות",
    excluded: "מוחרג",
    customText: "נוסח מותאם ({lang})",
    customTextHint: "השאירי ריק כדי להשתמש בנוסח הכללי. ביקור עם כמה שירותים תמיד מקבל את הנוסח הכללי.",
    interval: "ימים אחרי הביקור",
    intervalHint: "ריק = המרווח הכללי ({days} ימים)",
    price: "מחיר בהודעה",
    priceInherit: "מהתור / מהשירות",
    priceCustom: "מחיר מותאם",
    priceHide: "לא להציג מחיר",
    priceMissing: "אין מחיר שמור לשירות — המחיר לא יוצג (אף פעם לא כ־0).",
    reset: "חזרה לברירת המחדל",
    noServices: "עדיין אין שירותים בקטלוג.",
    multiServiceNote: "ביקור עם כמה שירותים מקבל הודעה אחת בנוסח הכללי עם רשימת השירותים.",
  },
  test: {
    title: "שליחת ניסיון",
    phone: "הטלפון שלך",
    version: "איזה נוסח לבדוק",
    outcome: "תוצאה מדומה",
    outcomeNormal: "נמסרה",
    outcomeTemporary: "תקלה זמנית",
    outcomeTimeout: "אין תשובה ברורה",
    outcomePermanent: "נדחתה",
    send: "שליחת ניסיון",
    sent: "ניסיון נשלח אל {phone}.",
    resultTitle: "הניסיון האחרון",
    needsConnection: "כדי לשלוח ניסיון צריך לחבר SMS.",
    forbidden: "אין לך הרשאה לשלוח ניסיון.",
  },
  history: {
    search: "חיפוש לקוחה או טלפון",
    automation: "סוג הודעה",
    status: "סטטוס",
    period: "תאריך",
    periodAll: "כל התאריכים",
    periodUpcoming: "קרובות",
    periodToday: "היום",
    period7: "7 הימים האחרונים",
    period30: "30 הימים האחרונים",
    includeTests: "להציג ניסיונות",
    empty: "אין הודעות שמתאימות לסינון.",
    emptySalon: "עדיין אין הודעות. הן יופיעו כאן כשייקבעו תורים.",
    clear: "ניקוי סינון",
    count: "{n:הודעה אחת|# הודעות}",
    colCustomer: "לקוחה",
    colAutomation: "הודעה",
    colVisit: "ביקור",
    colPlanned: "מתוכננת",
    colSent: "נשלחה",
    colStatus: "סטטוס",
    colUnits: "יחידות",
    colCost: "עלות",
    forecast: "תחזית",
  },
  drawer: {
    sentText: "הנוסח המדויק שנשלח",
    plannedText: "הנוסח המתוכנן",
    plannedTextNote: "רגע לפני השליחה בודקים שוב: התור, ההסכמה, האוטומציה והפרטים.",
    projectedBlock: "אם הייתה יוצאת עכשיו, היא לא הייתה נשלחת: {reason}",
    details: "פרטים",
    customer: "לקוחה",
    phone: "טלפון",
    visit: "ביקור",
    planned: "מתוכננת ל־",
    nextTry: "הניסיון הבא",
    sentAt: "נשלחה",
    deliveredAt: "נמסרה",
    failedAt: "נכשלה",
    units: "יחידות SMS",
    cost: "עלות",
    costEstimated: "{cost} · משוער",
    costActual: "{cost} · בפועל",
    versionUsed: "גרסת נוסח",
    language: "שפה",
    timeline: "מה קרה",
    created: "ההודעה תוכננה",
    attempt: "ניסיון {n}",
    attemptAccepted: "התקבלה בשירות ה־SMS",
    attemptTemporary: "תקלה זמנית בשירות ה־SMS",
    attemptPermanent: "נדחתה בשירות ה־SMS",
    attemptTimeout: "אין תשובה ברורה משירות ה־SMS",
    reconciledDelivered: "בדיקת סטטוס: נמסרה",
    reconciledNotReceived: "בדיקת סטטוס: לא התקבלה",
    deliveredEvent: "נמסרה לטלפון",
    failedEvent: "לא נמסרה",
    stoppedEvent: "נעצרה",
    deferredEvent: "נדחתה ל־{when}",
    mergedServices: "כוללת שירותים שאוחדו מהזמנה אחרת: {list}",
    mergedInto: "אוחדה להודעה אחרת",
    openMerged: "פתיחת ההודעה המאוחדת",
    cancel: "ביטול ההודעה",
    cancelled: "ההודעה בוטלה.",
    resume: "לנסות שוב עכשיו",
    checkStatus: "בדיקת סטטוס",
    simulateDelivered: "הדמיה: נמסרה",
    simulateNotReceived: "הדמיה: לא התקבלה",
    unknownNote: "כשהסטטוס לא ברור לא שולחים שוב, כדי שהלקוחה לא תקבל את אותה הודעה פעמיים. בדיקת הסטטוס קובעת מה הלאה.",
    diagnostics: "אבחון (טכני)",
    diagnosticsNote: "לתמיכה בלבד. לקוחות לא רואים את זה.",
    testBadge: "ניסיון",
    testTo: "ניסיון אל {phone}",
  },
  settings: {
    title: "הגדרות הודעות",
    subtitle: "חיבור, שם שולח, שעות ותקציב.",
    sectionSender: "שולח ושפה",
    sectionMarketing: "הודעות שיווקיות",
    sectionBudget: "תקציב ושליחה",
    budgetUsed: "נוצלו {spent} מתוך {limit} החודש.",
    connection: "חיבור",
    connectedAs: "מחובר ל־{provider}",
    connect: "חיבור SMS",
    disconnect: "ניתוק",
    senderName: "שם השולח",
    senderHint: "3–11 אותיות באנגלית או ספרות, כפי שהלקוחות יראו.",
    senderInvalid: "יש להזין 3–11 אותיות באנגלית או ספרות.",
    timezone: "אזור הזמן של הסלון",
    defaultLanguage: "שפת ברירת מחדל",
    languages: "שפות ההודעות",
    languagesHint: "לכל שפה נוסח שמור משלה. אין תרגום אוטומטי.",
    window: "שעות שיווק",
    windowHint: "הודעות שיווקיות מחוץ לשעות האלה עוברות למועד המותר הבא. הודעות שירות לא מוגבלות.",
    windowFrom: "מ־",
    windowTo: "עד",
    frequency: "מגבלת תדירות שיווקית",
    frequencyHint: "עד הודעה שיווקית אחת ללקוחה בכל פרק הימים הזה.",
    budget: "תקציב שיווק חודשי (₪)",
    budgetHint: "כשמגיעים אליו, הודעות שיווקיות מושהות ומתקבלת התראה.",
    alertAt: "התראה ב־(% מהתקציב)",
    attempts: "מספר ניסיונות מרבי להודעה",
    attemptsHint: "ניסיון חוזר רק אחרי תקלה זמנית, ותמיד על אותה הודעה.",
    unitCost: "מחיר משוער ליחידת SMS (₪)",
    unitCostHint: "משמש להערכות. העלות בפועל מגיעה מהספק (כאן מדומה).",
    save: "שמירת ההגדרות",
    saved: "ההגדרות נשמרו.",
    forbidden: "רק בעלת הסלון יכולה לשנות הגדרות SMS.",
    he: "עברית",
    en: "אנגלית",
  },
  simulator: {
    title: "סימולטור הדגמה",
    subtitle: "שליטה בעולם ההדגמה: הזזת השעון, יצירת אירועים ביומן ובחירת התשובה של שירות ה־SMS. משפיע רק על נתוני ההדגמה.",
    clock: "שעון מדומה",
    plus15: "‎+15 דק׳",
    plus1h: "‎+שעה",
    plus1d: "‎+יום",
    nextEvent: "קפיצה לאירוע הבא",
    noNextEvent: "אין משהו מתוזמן.",
    provider: "התשובה הבאה של שירות ה־SMS",
    providerHint: "חלה פעם אחת, על ההודעה הבאה שנשלחת.",
    role: "צפייה בתור",
    roleHint: "כך רואים איך ההרשאות משנות את מה שכל אחת יכולה לעשות.",
    reset: "איפוס נתוני ההדגמה",
    resetConfirm: "לאפס את כל נתוני ההדגמה? הגדרות הסלון לא יושפעו.",
    resetDone: "נתוני ההדגמה אופסו.",
    restartFirstRun: "להציג שוב את ההגדרה הראשונית",
    book: "קביעת ביקור",
    bookCustomer: "לקוחה",
    bookServices: "שירותים",
    bookDay: "בעוד כמה ימים",
    bookTime: "שעה",
    bookButton: "קביעה",
    booked: "הביקור נקבע. האישור מטופל ע״י האוטומציה.",
    upcoming: "ביקורים קרובים",
    noUpcoming: "אין ביקורים קרובים.",
    reschedule: "‎+יום",
    cancelVisit: "ביטול",
    complete: "סימון כהושלם",
    noShow: "לא הגיעה",
    customers: "לקוחות והסכמות",
    marketingConsent: "שיווק",
    transactionalConsent: "SMS שירות",
    consentGranted: "אושר",
    consentRevoked: "הוסרה",
    consentUnknown: "אין רישום",
    noPhone: "אין טלפון",
    undeliverable: "הטלפון לא מקבל",
    done: "בוצע.",
  },
  permissions: {
    noAccessTitle: "אין גישה לשיווק",
    noAccessBody: "רק בעלת הסלון או מנהלת עם הרשאה יכולות לנהל הודעות ללקוחות.",
    viewOnly: "צפייה בלבד",
    editOnly: "טיוטות בלבד",
  },
  empty: {
    salonNoData: "עדיין אין ביקורים ביומן",
    salonNoDataBody: "כשייקבעו ביקורים, ההודעות שהם יפעילו יופיעו כאן. אפשר לראות הכול עם נתוני הדגמה.",
    openDemo: "לצפייה עם נתוני הדגמה",
  },
  errors: {
    forbidden: "אין לך הרשאה לפעולה הזו.",
    invalid: "חלק מהפרטים לא תקינים.",
    interval_required: "יש לבחור ולפרסם מרווח זמן לפני ההפעלה.",
    not_connected: "קודם צריך לחבר SMS.",
    demo_only: "זמין רק במצב הדגמה.",
    not_found: "אין מה לעדכן.",
  },
  audit: {
    draft_saved: "טיוטה נשמרה",
    draft_discarded: "טיוטה נמחקה",
    published: "פורסם",
    enabled: "הופעלה",
    disabled: "כובתה",
    settings_updated: "הגדרות עודכנו",
    sms_connected: "SMS חובר",
    sms_disconnected: "SMS נותק",
    test_sent: "נשלח ניסיון",
    message_cancelled: "הודעה בוטלה",
    message_reconciled: "סטטוס נבדק",
    message_resumed: "הודעה חודשה",
    demo_reset: "הדגמה אופסה",
  },
};
